import type {
    IWalletFundOptions,
    IWalletFundPreset,
    WalletFundProvider,
    WalletPaymentInputMode,
} from '../../redux/buzzbridge/types';

export const WALLET_LEDGER_CURRENCY = 'usd';
const FALLBACK_MIN_USD_CENTS = 10_000;

/** Card charges in local currency when Stripe supports it; otherwise USD-only (e.g. GHS). */
export function chargesInPaymentCurrency(options: IWalletFundOptions | null): boolean {
    if (!options || options.defaulted_to_usd) return false;
    return options.payment_currency.toLowerCase() !== options.wallet_currency.toLowerCase();
}

export function getMinWalletTopUpCents(options: IWalletFundOptions | null): number {
    return (
        options?.minimum_fund?.wallet_credit?.amount_cents ??
        options?.minimum_fund?.wallet_credit_usd_cents ??
        FALLBACK_MIN_USD_CENTS
    );
}

export function isAdminTestPreset(preset: IWalletFundPreset | null | undefined): boolean {
    if (!preset) return false;
    return preset.purpose === 'admin_test_funding' || preset.fees_waived === true;
}

export function getAdminPresets(options: IWalletFundOptions | null): IWalletFundPreset[] {
    return options?.admin_presets?.length ? options.admin_presets : [];
}

export function getAllWalletFundPresets(options: IWalletFundOptions | null): IWalletFundPreset[] {
    if (!options) return [];
    return [...getAdminPresets(options), ...(options.presets ?? [])];
}

/** Minimum wallet credit required for checkout — lower for admin test preset when selected. */
export function getEffectiveMinWalletTopUpCents(
    options: IWalletFundOptions | null,
    selectedPreset?: IWalletFundPreset | null,
): number {
    if (selectedPreset && isAdminTestPreset(selectedPreset)) {
        return selectedPreset.wallet_credit.amount_cents;
    }
    return getMinWalletTopUpCents(options);
}

export function getCustomAmountInputMode(options: IWalletFundOptions | null): WalletPaymentInputMode {
    const fromApi = options?.instructions?.custom_amount_input_mode;
    if (fromApi === 'gross_payment' || fromApi === 'wallet_credit_usd') return fromApi;
    if (options && chargesInPaymentCurrency(options)) return 'gross_payment';
    return 'wallet_credit_usd';
}

export function isCustomGrossPaymentMode(options: IWalletFundOptions | null): boolean {
    return getCustomAmountInputMode(options) === 'gross_payment';
}

export function isWalletTopUpAmountAllowed(
    walletCreditUsdCents: number,
    options: IWalletFundOptions | null,
    selectedPreset?: IWalletFundPreset | null,
    paymentChargeCents?: number | null,
): boolean {
    if (selectedPreset && isAdminTestPreset(selectedPreset)) {
        return walletCreditUsdCents === selectedPreset.wallet_credit.amount_cents;
    }
    if (selectedPreset) {
        return walletCreditUsdCents === selectedPreset.wallet_credit.amount_cents;
    }
    if (paymentChargeCents != null && paymentChargeCents >= 100 && isCustomGrossPaymentMode(options)) {
        return paymentChargeCents >= getMinPaymentChargeCents(options);
    }
    if (!chargesInPaymentCurrency(options)) {
        return walletCreditUsdCents >= getMinWalletTopUpCents(options);
    }
    if (paymentChargeCents != null && paymentChargeCents >= 100) {
        return paymentChargeCents >= getMinPaymentChargeCents(options);
    }
    return walletCreditUsdCents >= getMinWalletTopUpCents(options);
}

export function getAdminTestPresetDisplayTitle(preset: IWalletFundPreset, options: IWalletFundOptions | null): string {
    const creditLabel =
        formatApiAmountLabel(
            preset.labels?.wallet_credit_primary ?? preset.wallet_credit?.formatted,
            options,
            preset,
        ) ??
        preset.labels?.wallet_credit_primary ??
        preset.wallet_credit?.formatted;
    if (creditLabel) {
        return `Test top-up — ${creditLabel} (no fees)`;
    }
    const cents = preset.wallet_credit?.amount_cents ?? options?.admin_test_funding_usd_cents;
    if (typeof cents === 'number' && cents > 0) {
        return `Test top-up — ${formatUsdWalletCents(cents)} (no fees)`;
    }
    return 'Test top-up (no fees)';
}

export function findMatchingPreset(
    options: IWalletFundOptions | null,
    walletCreditUsdCents: number,
): IWalletFundPreset | undefined {
    if (!options || walletCreditUsdCents <= 0) return undefined;
    return getAllWalletFundPresets(options).find(p => p.wallet_credit.amount_cents === walletCreditUsdCents);
}

export function getYouPayAmountCents(preset: IWalletFundPreset): number {
    return preset.you_pay?.amount_cents ?? preset.payment?.customer_charge_cents ?? 0;
}

export function getYouPayFormatted(preset: IWalletFundPreset): string {
    return preset.labels?.you_pay ?? preset.you_pay?.formatted ?? preset.payment?.customer_charge_formatted ?? '';
}

/** Add thousands separators to the numeric part of a currency label (e.g. ₦191806.43 → ₦191,806.43). */
export function formatAmountLabelWithThousands(label: string): string {
    const trimmed = label.trim();
    if (!trimmed) return trimmed;

    const isoPrefix = trimmed.match(/^([A-Z]{3})\s+(.+)$/);
    if (isoPrefix) {
        const sym = CURRENCY_SYMBOL_FALLBACK[isoPrefix[1]] ?? isoPrefix[1];
        return formatAmountLabelWithThousands(`${sym}${isoPrefix[2].trim()}`);
    }

    const symMatch = trimmed.match(/^([₦$£€])\s*(.+)$/);
    if (symMatch) {
        const sym = symMatch[1];
        const rest = symMatch[2].replace(/,/g, '').trim();
        const numericMatch = rest.match(/^([\d.]+)(.*)$/);
        const numericPart = numericMatch?.[1] ?? '';
        const suffix = numericMatch?.[2]?.trim() ?? '';
        const formatted = formatMajorUnitsString(numericPart);
        if (!formatted) return trimmed;
        const amountLabel = sym === '$' ? `$${formatted}` : `${sym}${formatted}`;
        return suffix ? `${amountLabel} ${suffix}` : amountLabel;
    }

    if (/^[\d.,]+$/.test(trimmed)) {
        return formatMajorUnitsString(trimmed.replace(/,/g, ''));
    }

    return trimmed;
}

function formatMajorUnitsString(num: string): string {
    if (!num) return '';
    const [intPart, decPart] = num.split('.');
    const intFmt = Number(intPart || 0).toLocaleString('en-US');
    if (decPart !== undefined) return `${intFmt}.${decPart}`;
    return intFmt;
}

/** Parse display input with commas into major currency units (e.g. "23,000,000.5" → 23000000.5). */
export function parseAmountInputMajor(display: string): number {
    const cleaned = display.replace(/,/g, '').trim();
    if (!cleaned || cleaned === '.') return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
}

/** Format amount field as user types (commas + up to 2 decimal places). */
export function formatAmountInputMajor(raw: string): string {
    const cleaned = raw.replace(/,/g, '');
    let normalized = '';
    let sawDot = false;
    for (const ch of cleaned) {
        if (ch >= '0' && ch <= '9') normalized += ch;
        else if (ch === '.' && !sawDot) {
            normalized += '.';
            sawDot = true;
        }
    }
    return formatMajorUnitsString(normalized);
}

/** Prefer API formatted strings; swap ISO codes for symbols and add thousands separators. */
export function formatApiAmountLabel(
    value: string | undefined,
    options: IWalletFundOptions | null,
    preset?: IWalletFundPreset | null,
): string | undefined {
    if (!value) return undefined;
    let label = value.trim();
    if (!/^[₦$£€]/.test(label)) {
        const isoPrefix = label.match(/^([A-Z]{3})\s+(.+)$/);
        if (isoPrefix) {
            const sym = CURRENCY_SYMBOL_FALLBACK[isoPrefix[1]] ?? getPaymentCurrencySymbol(options, preset);
            label = `${sym}${isoPrefix[2].trim()}`;
        }
    }
    return formatAmountLabelWithThousands(label);
}

/** Formatted "you pay" line for preset buttons and summaries. */
export function formatYouPayDisplay(preset: IWalletFundPreset, options: IWalletFundOptions | null): string {
    const fromApi = formatApiAmountLabel(getYouPayFormatted(preset), options, preset);
    if (fromApi) return fromApi;
    const cents = getYouPayAmountCents(preset);
    return cents > 0 ? formatChargeFromOptions(cents, options, preset) : '';
}

export function getPresetFeesFormatted(
    preset: IWalletFundPreset,
    options: IWalletFundOptions | null,
): string | undefined {
    return formatApiAmountLabel(preset.you_pay?.fees_formatted, options, preset);
}

/** Minimum wallet credit shown in payment currency (from fund/options). */
export function getMinimumWalletCreditPaymentFormatted(options: IWalletFundOptions | null): string | undefined {
    const min = options?.minimum_fund;
    if (!min) return undefined;
    if (min.wallet_credit_payment_formatted) {
        return (
            formatApiAmountLabel(min.wallet_credit_payment_formatted, options) ?? min.wallet_credit_payment_formatted
        );
    }
    if (typeof min.wallet_credit_payment_cents === 'number' && min.wallet_credit_payment_cents > 0) {
        return formatChargeFromOptions(min.wallet_credit_payment_cents, options);
    }
    return (
        formatApiAmountLabel(min.labels?.wallet_credit_primary, options) ??
        formatApiAmountLabel(min.wallet_credit_local_equivalent?.formatted, options)
    );
}

/** Processing / service fees in payment currency (cents) from a fund-options preset. */
export function getPresetProcessingFeesCents(preset: IWalletFundPreset): number {
    const yp = preset.you_pay;
    if (!yp) return 0;
    if (typeof yp.fees_cents === 'number' && yp.fees_cents > 0) return yp.fees_cents;
    if (typeof yp.platform_fee_total_cents === 'number' && yp.platform_fee_total_cents > 0) {
        return yp.platform_fee_total_cents;
    }
    const charge = getYouPayAmountCents(preset);
    const tax = yp.tax_cents ?? 0;
    const custFees = yp.customer_fees_cents ?? 0;
    if (custFees > 0 && charge > 0 && custFees < charge - tax) return custFees;
    const baseMajor = preset.conversion?.local_wallet_equivalent_major;
    if (charge > 0 && typeof baseMajor === 'number' && baseMajor > 0) {
        const baseCents = Math.round(baseMajor * 100);
        const derived = charge - tax - baseCents;
        if (derived > 0 && derived < charge) return derived;
    }
    return 0;
}

export function getPresetTaxCents(preset: IWalletFundPreset): number {
    return preset.you_pay?.tax_cents ?? 0;
}

export function getPaymentCurrencySymbol(
    options: IWalletFundOptions | null,
    preset?: IWalletFundPreset | null,
): string {
    if (preset?.you_pay?.symbol) return preset.you_pay.symbol;
    const formatted = preset?.you_pay?.formatted ?? preset?.payment?.customer_charge_formatted;
    if (formatted) {
        const sym = formatted.replace(/[\d.,\s]/g, '').trim();
        if (sym) return sym;
    }
    if (!options) return '$';
    if (options.defaulted_to_usd) return '$';
    const code = options.payment_currency.toUpperCase();
    if (CURRENCY_SYMBOL_FALLBACK[code]) return CURRENCY_SYMBOL_FALLBACK[code];
    try {
        const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).formatToParts(0);
        const sym = parts.find(p => p.type === 'currency')?.value;
        if (sym && sym.length <= 3) return sym;
    } catch {
        /* fall through */
    }
    return '$';
}

export function formatUsdWalletCents(cents: number): string {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CURRENCY_SYMBOL_FALLBACK: Record<string, string> = {
    USD: '$',
    NGN: '₦',
    GBP: '£',
    EUR: '€',
};

/** Format a card-charge amount with the payment symbol (₦, $, …) — not ISO currency codes like "NGN". */
export function formatChargeAmount(cents: number, symbol: string): string {
    const major = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!symbol || symbol === '$') return `$${major}`;
    if (symbol.length === 1 || symbol === '₦') return `${symbol}${major}`;
    return `${symbol} ${major}`;
}

export function formatChargeFromOptions(
    cents: number,
    options: IWalletFundOptions | null,
    preset?: IWalletFundPreset | null,
): string {
    if (!options || options.defaulted_to_usd) return formatUsdWalletCents(cents);
    const symbol = getPaymentCurrencySymbol(options, preset);
    return formatChargeAmount(cents, symbol);
}

/** @deprecated Prefer formatChargeFromOptions — Intl often emits "NGN" instead of "₦". */
export function formatPaymentCurrencyCents(cents: number, currency: string): string {
    const code = currency.toUpperCase();
    const symbol = CURRENCY_SYMBOL_FALLBACK[code];
    if (symbol) return formatChargeAmount(cents, symbol);
    try {
        const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).formatToParts(cents / 100);
        const sym = parts.find(p => p.type === 'currency')?.value;
        const num = parts
            .filter(p => p.type === 'integer' || p.type === 'group' || p.type === 'decimal' || p.type === 'fraction')
            .map(p => p.value)
            .join('');
        if (sym && num) return formatChargeAmount(cents, sym);
    } catch {
        /* fall through */
    }
    return formatChargeAmount(cents, CURRENCY_SYMBOL_FALLBACK[code] ?? code);
}

/** Short copy above payment form when amount is below minimum. */
export function getMinimumSelectPresetPrompt(options: IWalletFundOptions | null): string {
    const usd =
        options?.minimum_fund?.wallet_credit_usd_formatted ??
        options?.minimum_fund?.wallet_credit?.formatted ??
        formatUsdWalletCents(getMinWalletTopUpCents(options));
    const payment = getMinimumWalletCreditPaymentFormatted(options);
    if (options && chargesInPaymentCurrency(options) && payment) {
        return `Select a preset or enter at least ${payment} (${usd} USD wallet credit) to pay.`;
    }
    return `Select a preset or enter at least ${usd} USD wallet credit to pay.`;
}

/** Pay button label when amount is below minimum. */
export function getMinimumEnterAmountPrompt(options: IWalletFundOptions | null): string {
    if (options && isCustomGrossPaymentMode(options)) {
        const minCharge =
            formatApiAmountLabel(options.minimum_fund?.minimum_you_pay_formatted, options) ??
            formatApiAmountLabel(options.minimum_fund?.labels?.you_pay, options) ??
            (options.minimum_fund?.you_pay?.amount_cents
                ? formatChargeFromOptions(options.minimum_fund.you_pay.amount_cents, options)
                : null);
        if (minCharge) return `Enter card charge (${minCharge} minimum)`;
    }
    const payment = getMinimumWalletCreditPaymentFormatted(options);
    if (options && chargesInPaymentCurrency(options) && payment) {
        return `Enter an amount (${payment} minimum)`;
    }
    const usd =
        options?.minimum_fund?.wallet_credit_usd_formatted ??
        options?.minimum_fund?.wallet_credit?.formatted ??
        formatUsdWalletCents(getMinWalletTopUpCents(options));
    return `Enter an amount (${usd} minimum)`;
}

export function getMinimumFundLabel(options: IWalletFundOptions | null): string {
    const usd =
        options?.minimum_fund?.wallet_credit_usd_formatted ??
        options?.minimum_fund?.wallet_credit?.formatted ??
        formatUsdWalletCents(getMinWalletTopUpCents(options));

    if (!options) return `Minimum ${usd} wallet credit`;

    const paymentCredit = getMinimumWalletCreditPaymentFormatted(options);
    const minCharge =
        formatApiAmountLabel(options.minimum_fund?.minimum_you_pay_formatted, options) ??
        formatApiAmountLabel(options.minimum_fund?.labels?.you_pay, options) ??
        options.minimum_fund?.you_pay?.formatted ??
        (options.minimum_fund?.you_pay?.amount_cents
            ? formatChargeFromOptions(options.minimum_fund.you_pay.amount_cents, options)
            : null);

    if (chargesInPaymentCurrency(options) && paymentCredit) {
        const payNote = minCharge ? ` · card charge from ${minCharge}` : '';
        return `Minimum ${paymentCredit} (${usd} USD wallet credit)${payNote}`;
    }
    return `Minimum ${usd} wallet credit`;
}

/** USD amount string for custom amount field when card charges in USD. */
export function presetToUsdInputAmount(preset: IWalletFundPreset): string {
    return String(preset.wallet_credit.amount_usd);
}

/** Wallet credit in payment currency (minor units) — excludes fees/tax. */
export function getPresetWalletCreditPaymentCents(preset: IWalletFundPreset): number {
    if (typeof preset.you_pay?.wallet_credit_cents === 'number' && preset.you_pay.wallet_credit_cents > 0) {
        return preset.you_pay.wallet_credit_cents;
    }
    const major =
        preset.wallet_credit_local_equivalent?.amount_major ?? preset.conversion?.local_wallet_equivalent_major;
    if (typeof major === 'number' && major > 0) {
        return Math.round(major * 100);
    }
    const rate = preset.conversion?.rate_usd_to_local ?? preset.wallet_credit_local_equivalent?.rate;
    if (rate && preset.wallet_credit.amount_cents > 0) {
        return Math.round((preset.wallet_credit.amount_cents / 100) * rate * 100);
    }
    return 0;
}

/** Payment-currency amount string for custom field — wallet credit local equivalent (not you_pay). */
export function presetToPaymentInputAmount(preset: IWalletFundPreset): string {
    const major =
        preset.wallet_credit_local_equivalent?.amount_major ??
        preset.conversion?.local_wallet_equivalent_major ??
        getPresetWalletCreditPaymentCents(preset) / 100;
    const raw = Number.isInteger(major) ? String(major) : major.toFixed(2).replace(/\.?0+$/, '');
    return formatAmountInputMajor(raw);
}

/** USD wallet credit amount for custom field (formatted with commas). */
export function presetToUsdInputAmountFormatted(preset: IWalletFundPreset): string {
    return formatAmountInputMajor(String(preset.wallet_credit.amount_usd));
}

export function estimateWalletCreditFromCharge(chargeCents: number, options: IWalletFundOptions | null): number {
    if (chargeCents <= 0) return 0;
    const ratioPreset = options?.presets[0];
    if (ratioPreset && getYouPayAmountCents(ratioPreset) > 0) {
        return Math.round((chargeCents * ratioPreset.wallet_credit.amount_cents) / getYouPayAmountCents(ratioPreset));
    }
    const rate = options?.exchange_rate?.rate;
    if (rate && rate > 0) return Math.round(chargeCents / rate);
    return 0;
}

export function getMinPaymentChargeCents(options: IWalletFundOptions | null): number {
    const fromApi = options?.minimum_fund?.you_pay?.amount_cents;
    if (typeof fromApi === 'number' && fromApi > 0) return fromApi;
    const usdMin = getMinWalletTopUpCents(options);
    if (!options || !chargesInPaymentCurrency(options)) return usdMin;
    const ratioPreset = options.presets[0];
    if (ratioPreset && ratioPreset.wallet_credit.amount_cents > 0) {
        return Math.max(
            100,
            Math.round((usdMin * getYouPayAmountCents(ratioPreset)) / ratioPreset.wallet_credit.amount_cents),
        );
    }
    const rate = options.exchange_rate?.rate;
    if (rate && rate > 0) return Math.max(100, Math.round(usdMin * rate));
    return usdMin;
}

export function buildFundWalletPayload(
    walletCreditUsdCents: number,
    options: IWalletFundOptions | null,
    preset?: IWalletFundPreset | null,
): { amount: number; currency: string } {
    if (preset?.fund_request) {
        return { amount: preset.fund_request.amount, currency: preset.fund_request.currency };
    }
    const currency = (options?.payment_currency ?? WALLET_LEDGER_CURRENCY).toLowerCase();
    return { amount: walletCreditUsdCents, currency };
}

export type WalletFeeCalcPayloadOpts = {
    /** Preset the user explicitly chose — not an auto-matched preset from a custom amount */
    selectedPreset?: IWalletFundPreset | null;
    /** Custom card-charge amount (minor units) when the user types in payment currency */
    paymentChargeCents?: number;
    /** Paystack / Stripe — must match on sales-calc and fund for processor fee parity */
    provider?: WalletFundProvider;
};

/**
 * Convert USD wallet credit (cents) to payment-currency minor units (kobo/cents) via FX.
 * sales-calc adds fees on top — do not use fee-inclusive you_pay ratios here.
 */
export function walletCreditToPaymentChargeCents(
    walletCreditUsdCents: number,
    options: IWalletFundOptions | null,
): number {
    if (walletCreditUsdCents <= 0) return 0;
    const rate = options?.exchange_rate?.rate;
    if (rate && rate > 0) {
        return Math.max(100, Math.round((walletCreditUsdCents / 100) * rate * 100));
    }
    const usdMin = getMinWalletTopUpCents(options);
    const minPayCents = options?.minimum_fund?.you_pay?.amount_cents;
    if (minPayCents && usdMin > 0) {
        return Math.max(100, Math.round(walletCreditUsdCents * (minPayCents / usdMin)));
    }
    return Math.max(walletCreditUsdCents, 100);
}

export type WalletFeeCalcPayload = {
    type: 'campaign_wallet';
    payment_input_mode: WalletPaymentInputMode;
    amount: number;
    currency: string;
    symbol: string;
    wallet_currency: string;
    wallet_credit_cents?: number;
    provider?: WalletFundProvider;
};

function withSalesCalcProvider(payload: WalletFeeCalcPayload, provider?: WalletFundProvider): WalletFeeCalcPayload {
    if (provider) return { ...payload, provider };
    return payload;
}

/** POST /mixed/sales-calc body for campaign wallet funding. */
export function buildWalletFeeCalcPayload(
    walletCreditUsdCents: number,
    options: IWalletFundOptions | null,
    opts?: WalletFeeCalcPayloadOpts,
): WalletFeeCalcPayload {
    const paymentCurrency = (options?.payment_currency ?? WALLET_LEDGER_CURRENCY).toLowerCase();
    const chargesInLocal = chargesInPaymentCurrency(options);
    const chargeCurrency = chargesInLocal ? paymentCurrency : WALLET_LEDGER_CURRENCY;
    const selected = opts?.selectedPreset;
    const matched = options ? findMatchingPreset(options, walletCreditUsdCents) : undefined;
    const presetForQuote =
        selected && selected.wallet_credit.amount_cents === walletCreditUsdCents
            ? selected
            : matched && matched.wallet_credit.amount_cents === walletCreditUsdCents
            ? matched
            : undefined;
    const symbol = getPaymentCurrencySymbol(options, presetForQuote ?? selected ?? matched);
    const provider = opts?.provider;

    const base = {
        type: 'campaign_wallet' as const,
        currency: chargeCurrency,
        symbol,
        wallet_currency: WALLET_LEDGER_CURRENCY,
    };

    // Admin test top-up — use fund_request.amount from server (no fee markup)
    if (selected && isAdminTestPreset(selected)) {
        const amount = selected.fund_request?.amount ?? selected.wallet_credit.amount_cents;
        const currency = (selected.fund_request?.currency ?? WALLET_LEDGER_CURRENCY).toLowerCase();
        return withSalesCalcProvider(
            {
                ...base,
                payment_input_mode: 'wallet_credit_usd',
                amount,
                currency,
                symbol: getPaymentCurrencySymbol(options, selected),
                wallet_credit_cents: selected.wallet_credit.amount_cents,
            },
            provider,
        );
    }

    // User-typed gross card charge in payment currency (kobo) — omit wallet_credit_cents
    if (
        opts?.paymentChargeCents &&
        opts.paymentChargeCents >= 100 &&
        chargesInLocal &&
        isCustomGrossPaymentMode(options)
    ) {
        return withSalesCalcProvider(
            {
                ...base,
                payment_input_mode: 'gross_payment',
                amount: opts.paymentChargeCents,
            },
            provider,
        );
    }

    // Explicit preset — wallet credit local equivalent in payment-currency minor units (excl. fees)
    if (presetForQuote) {
        return withSalesCalcProvider(
            {
                ...base,
                payment_input_mode: 'wallet_credit_payment',
                amount: getPresetWalletCreditPaymentCents(presetForQuote),
                wallet_credit_cents: walletCreditUsdCents,
            },
            provider,
        );
    }

    if (chargesInLocal) {
        return withSalesCalcProvider(
            {
                ...base,
                payment_input_mode: 'wallet_credit_payment',
                amount: walletCreditToPaymentChargeCents(walletCreditUsdCents, options),
                wallet_credit_cents: walletCreditUsdCents,
            },
            provider,
        );
    }

    const usdAmount = Math.max(walletCreditUsdCents, 100);
    return withSalesCalcProvider(
        {
            ...base,
            payment_input_mode: 'wallet_credit_usd',
            amount: usdAmount,
            wallet_credit_cents: usdAmount,
        },
        provider,
    );
}

export function feePreviewMatchesWalletCredit(
    fee: { amount?: number; wallet_credit_cents?: number } | null,
    walletCreditUsdCents: number,
    options: IWalletFundOptions | null,
): boolean {
    if (!fee) return false;
    if (typeof fee.wallet_credit_cents === 'number') {
        return fee.wallet_credit_cents === walletCreditUsdCents;
    }
    if (!chargesInPaymentCurrency(options)) {
        return fee.amount === walletCreditUsdCents;
    }
    return false;
}

export function resolveActivePreset(
    options: IWalletFundOptions | null,
    selectedPreset: IWalletFundPreset | null | undefined,
    walletCreditUsdCents: number,
): IWalletFundPreset | undefined {
    if (selectedPreset) return selectedPreset;
    return findMatchingPreset(options, walletCreditUsdCents);
}
